import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { chiffrer, commander } from '../lib/orders/gateway.supabase';
import { tarifsPourEnfant } from '../lib/orders/tarifs.gateway';
import {
  SEUIL_REVENUS_CENTIMES, texteDeclaration, type Commande, type Devis,
} from '../lib/orders/devis';

export interface ParentResult {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  telephone: string;
  nnational: string | null;
}

export interface Kid {
  id: string;
  prenom: string;
  nom: string;
  date_naissance: string;
  cpostal: string | null;
  ecole: string | null;
  inclusion: { has_needs: boolean } | null;
}

export interface SessionResult {
  id: string;
  start_date: string;
  end_date: string;
  capacity: number;
  current_registrations: number;
  periode: string;
  semaine: string | null;
  prix_normal: number;
  prix_reduit: number | null;
  prix_local: number | null;
  prix_local_reduit: number | null;
  tarif_condition_id: string | null;
  stage: { id: string; title: string; age_min: number; age_max: number; image_url: string | null };
  center: { id: string; name: string };
  calculatedPrice: number;
  calculatedReducedPrice: number | null;
  isLocal: boolean;
  alreadyRegistered: boolean;
}

/*
  Le panier de l'ecran d'inscription admin. Comme celui du parent, il ne porte
  plus ni `price` ni `price_type` : le tarif est resolu en base.
*/
export interface CartItem {
  id: string;
  activity_id: string;
  kid_id: string;
  kidName: string;
  activityName: string;
  activityCategory: string;
  dateRange: string;
  imageUrl?: string;
}

interface SessionFilters {
  centerId?: string;
  periode?: string;
}

function calculateAgeAtDate(birthDate: string, referenceDate: string): number {
  const birth = new Date(birthDate);
  const ref = new Date(referenceDate);

  let years = ref.getFullYear() - birth.getFullYear();
  let months = ref.getMonth() - birth.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  } else if (months === 0 && ref.getDate() < birth.getDate()) {
    years--;
    months = 11;
  }

  if (months > 0 && ref.getDate() < birth.getDate()) {
    months--;
  }

  return parseFloat((years + (months / 12)).toFixed(1));
}

interface AdminRegistrationState {
  parents: ParentResult[];
  kids: Kid[];
  sessions: SessionResult[];
  periodes: string[];
  isSearching: boolean;
  isLoadingKids: boolean;
  isLoadingSessions: boolean;
  isSubmitting: boolean;

  searchParents: (query: string) => Promise<void>;
  fetchKidsForParent: (parentId: string) => Promise<void>;
  fetchAvailableSessions: (kid: Kid, filters?: SessionFilters) => Promise<void>;
  quoteAdminRegistration: (
    parentId: string, items: CartItem[], reduitDeclare: boolean, codes: string[],
  ) => Promise<Devis>;
  createAdminRegistration: (
    parentId: string, items: CartItem[], reduitDeclare: boolean, codes: string[],
  ) => Promise<{ invoiceNumber: string; commande: Commande }>;
  reset: () => void;
}

export const useAdminRegistrationStore = create<AdminRegistrationState>((set, get) => ({
  parents: [],
  kids: [],
  sessions: [],
  periodes: [],
  isSearching: false,
  isLoadingKids: false,
  isLoadingSessions: false,
  isSubmitting: false,

  searchParents: async (query: string) => {
    set({ isSearching: true });
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, nom, prenom, telephone, nnational')
        .or(`email.ilike.%${query}%,nom.ilike.%${query}%,prenom.ilike.%${query}%,nnational.ilike.%${query}%`)
        .order('nom')
        .limit(20);

      if (error) throw error;
      set({ parents: data || [] });
    } finally {
      set({ isSearching: false });
    }
  },

  fetchKidsForParent: async (parentId: string) => {
    set({ isLoadingKids: true });
    try {
      const { data, error } = await supabase
        .from('kids')
        .select('id, prenom, nom, date_naissance, cpostal, ecole, inclusion:kid_inclusion(has_needs)')
        .eq('user_id', parentId)
        .order('prenom');

      if (error) throw error;
      const kids = (data || []).map((k: any) => ({
        ...k,
        inclusion: Array.isArray(k.inclusion) ? k.inclusion[0] || null : k.inclusion,
      }));
      set({ kids });
    } finally {
      set({ isLoadingKids: false });
    }
  },

  fetchAvailableSessions: async (kid: Kid, filters?: SessionFilters) => {
    set({ isLoadingSessions: true });
    try {
      const today = new Date().toISOString().split('T')[0];

      let query = supabase
        .from('sessions')
        .select(`
          id, start_date, end_date, capacity, current_registrations, periode, semaine,
          prix_normal, prix_reduit, prix_local, prix_local_reduit, tarif_condition_id,
          stage:stages(id, title, age_min, age_max, image_url),
          center:centers!center_id(id, name)
        `)
        .eq('active', true)
        .eq('archived', false)
        .gte('end_date', today)
        /*
          L'ecran d'administration voit AUSSI les sessions pas encore
          ouvertes. Inscrire une famille par telephone avant l'ouverture est un
          geste normal du back-office, et le declencheur en base en exempte
          explicitement les administrateurs : leur cacher ces sessions ici
          leur retirait le moyen d'exercer une permission qu'ils ont.
        */
        .order('start_date');

      if (filters?.centerId) query = query.eq('center_id', filters.centerId);
      if (filters?.periode) query = query.eq('periode', filters.periode);

      const [sessionsResult, registrationsResult] = await Promise.all([
        query,
        supabase
          .from('registrations')
          .select('activity_id')
          .eq('kid_id', kid.id)
          .not('cancellation_status', 'like', 'cancelled_%')
      ]);

      if (sessionsResult.error) throw sessionsResult.error;

      const registeredSessionIds = new Set(
        (registrationsResult.data || []).map(r => r.activity_id)
      );

      /*
        Les sessions dont l'age convient, avant tarification. Le filtre d'age
        est une regle d'ecran, pas une regle d'argent : il reste ici.
      */
      const eligibles: any[] = [];
      const uniquePeriodes = new Set<string>();

      for (const session of sessionsResult.data || []) {
        const stage = Array.isArray(session.stage) ? session.stage[0] : session.stage;
        const center = Array.isArray(session.center) ? session.center[0] : session.center;
        if (!stage || !center) continue;

        uniquePeriodes.add(session.periode);

        const ageAtStart = calculateAgeAtDate(kid.date_naissance, session.start_date);
        if (ageAtStart < stage.age_min || ageAtStart >= stage.age_max) continue;

        eligibles.push({ ...session, stage, center });
      }

      /*
        Les tarifs, en UN aller-retour, par la fonction que la facture utilise.

        Ce bloc remplace une reimplementation de la regle du tarif local — la
        deuxieme du depot — qui ne testait pas `tarif_conditions.active` :
        l'administrateur voyait donc un tarif local que le parent ne voyait
        pas, la RLS lui cachant les conditions inactives. Le prix ne depend
        plus de qui regarde.

        Les deux tarifs sont demandes, normal et reduit, parce que l'ecran
        montre les deux avant que la declaration ne soit faite.
      */
      const [tarifsNormaux, tarifsReduits] = await Promise.all([
        tarifsPourEnfant(kid.id, eligibles.map((s) => s.id), false),
        tarifsPourEnfant(kid.id, eligibles.map((s) => s.id), true),
      ]);

      const filtered: SessionResult[] = eligibles.map((session) => {
        const normal = tarifsNormaux.get(session.id);
        const reduit = tarifsReduits.get(session.id);
        return {
          ...session,
          calculatedPrice: (normal?.amount_cents ?? 0) / 100,
          calculatedReducedPrice: reduit ? reduit.amount_cents / 100 : null,
          isLocal: normal?.local_applied ?? false,
          alreadyRegistered: registeredSessionIds.has(session.id)
        };
      });

      set({ sessions: filtered, periodes: [...uniquePeriodes].sort() });
    } finally {
      set({ isLoadingSessions: false });
    }
  },

  /*
    L'inscription par l'administrateur passe par la MEME fonction que celle du
    parent. Elle remplace un `fetch` sur `create-invoice`, qui additionnait les
    prix envoyes par le navigateur et n'etait pas atomique : une commande
    interrompue laissait des inscriptions sans facture.

    `create_order` autorise un administrateur a commander pour une famille ;
    le controle est dans la fonction, pas ici.
  */
  quoteAdminRegistration: async (parentId, items, reduitDeclare, codes) => {
    return chiffrer(
      parentId,
      items.map((i) => ({ session_id: i.activity_id, kid_id: i.kid_id })),
      reduitDeclare,
      codes,
    );
  },

  createAdminRegistration: async (parentId, items, reduitDeclare, codes) => {
    set({ isSubmitting: true });
    try {
      const commande = await commander(
        parentId,
        items.map((i) => ({ session_id: i.activity_id, kid_id: i.kid_id })),
        reduitDeclare,
        reduitDeclare
          ? { threshold_cents: SEUIL_REVENUS_CENTIMES, statement_text: texteDeclaration() }
          : null,
        codes,
      );
      return { invoiceNumber: commande.invoice_number, commande };
    } finally {
      set({ isSubmitting: false });
    }
  },

  reset: () => {
    set({
      parents: [],
      kids: [],
      sessions: [],
      periodes: [],
      isSearching: false,
      isLoadingKids: false,
      isLoadingSessions: false,
      isSubmitting: false,
    });
  },
}));
